import { RouteProp } from '@react-navigation/native';
import * as Linking from 'expo-linking';
import React, { useEffect } from 'react';
import { ProfilesTabParamList, useProfilesTabNavigation } from '../../../../../utils/appNavigationNames';
import useMedicalProfileFiles from '../../../../../hooks/api/useMedicalProfileFiles';
import { MedicalProfileFiles } from '../../../../../primary-record-types';
import { getIntl } from '../../../../../utils/intl';
import { useHasPermission } from '../../../../v2BaseComponents/Permissions/hooks';
import { usePromptButton } from '../../../../shared/PromptButton';
import FolderSelection from '../../../../shared/FolderSelection';
import { AnimateLoadedContent } from '../../../../v2BaseComponents/Loader';
import { ActionButtonLayout } from '../../../../v2BaseComponents/ActionButtonLayout';
import ProfileHeaderContainer from '../../../../v2BaseComponents/ProfileHeaderContainer';
import { Action } from '../../../../v2BaseComponents/ActionButtonLayout/types';
import { TabView } from '../../../../v2BaseComponents/TabView';
import FileDetails from './FileDetails';
import FileContents from './FileContents';
import { useEditFileAction } from './useEditFileAction';
import { Platform } from 'react-native';

const intl = getIntl('FileScreen', {
    name: 'Name',
    contentType: 'Content Type',
    uploadedAt: 'Uploaded At',
    uploadedBy: 'Uploaded By',
    userNoLongerExists: 'User no longer exists',
    download: 'Download',
    deleteFile: 'Delete File',
    shortSummary: 'Short Summary',
    reprocess: 'Reprocess',
    fullSummary: 'Full Summary:',

    promptHeader: 'Move "{name}"',
    moveFile: 'Move File',
    summary: 'Summary',
    file: 'File',
    details: 'Details',
    sourceNote: 'Source Note',
});

export type FileScreenOwnProps = {};

export type FileScreenProps = FileScreenOwnProps & {
    route: RouteProp<ProfilesTabParamList, 'file'>;
};

type Tab = 'details' | 'file';

const FileScreen: React.FC<FileScreenProps> = ({
    route: {
        params: { fileId, profileId, page, startTime, ...boundingBoxParams },
    },
}) => {
    const hasFilesEdit = useHasPermission(profileId, 'files', 'edit');
    const { navigate } = useProfilesTabNavigation();
    const {
        deleteFile,
        isDeletingFile,
        isLoadingFile,
        file,
        isFetchingDownloadUrl,
        fetchDownloadUrl,
        reprocess,
        isReprocessing,
        onMoveFile,
        isMovingFile,
    } = useMedicalProfileFiles({
        medicalProfileId: profileId,
        medicalProfileFileId: fileId,
        onFetchedDownloadUrl(downloadUrl) {
            Platform.OS === 'web' ? window.open(downloadUrl, '_blank') : Linking.openURL(downloadUrl);
        },
        onDeleteFileSuccess: () => {
            navigate('files', { profileId });
        },
        onReprocessSuccess: () => {
            navigate('files', { profileId });
        },
    });
    const editFileAction = useEditFileAction({
        medicalProfileFileId: fileId,
        medicalProfileId: profileId,
    });
    const [selectedTab, setSelectedTab] = React.useState<Tab | null>(null);
    useEffect(() => {
        if (selectedTab === null && file) {
            setSelectedTab(file.processingStatus === 'processed' ? 'file' : 'details');
        }
    }, [file, selectedTab]);
    const isNoteFile = file?.sourceEntityType === 'note';
    const moveFilePrompt = usePromptButton({
        promptTitle: intl('promptHeader', { name: file?.fileName || '' }),
        renderPromptContents: (onClose) => (
            <FolderSelection
                startFolderId={'root'}
                onSelectFolder={(newParentFolderId) => {
                    onMoveFile({
                        medicalProfileFileId: fileId,
                        newParentFolderId,
                    });
                }}
                onClose={onClose}
                medicalProfileId={profileId}
            />
        ),
    });

    const fileExtension = file?.fileName.split('.').pop() || '';
    const disableActions = isFetchingDownloadUrl || isDeletingFile || isReprocessing || isMovingFile;

    // Some failures are properties of the file rather than of the attempt, so a retry is guaranteed to
    // fail again. This gates the page-level action; FileContents gates the one inside the error alert.
    const canReprocess = !(
        file?.failureType && MedicalProfileFiles.NON_REPROCESSABLE_FAILURE_TYPES.includes(file.failureType)
    );

    const actions: Action[] = [
        {
            icon: 'Download',
            title: intl('download'),
            onPress: () => {
                fetchDownloadUrl(fileId);
            },
            isDisabled: disableActions,
            testID: 'download-file',
        },
        editFileAction.action,
        {
            icon: 'Move',
            title: intl('moveFile', { name: file?.fileName || '' }),
            onPress: moveFilePrompt.onPress,
            isDisabled: disableActions,
            show: hasFilesEdit && !isNoteFile,
            testID: 'move-file',
        },
        {
            icon: 'RefreshCw',
            title: intl('reprocess'),
            onPress: () => {
                reprocess(fileId);
            },
            isDisabled: disableActions,
            show: hasFilesEdit && canReprocess,
            testID: 'reprocess-file',
        },
        {
            icon: 'Trash2',
            title: intl('deleteFile'),
            onPress: () => {
                deleteFile(fileId);
            },
            isDisabled: disableActions,
            variant: 'alert',
            show: hasFilesEdit && !isNoteFile,
            testID: 'delete-file',
        },
    ];

    const renderInActionButtonLayout = (isScrollable: boolean, content: React.ReactNode) => (
        <ActionButtonLayout whiteBackground scrollable={isScrollable} actions={actions}>
            {content}
        </ActionButtonLayout>
    );

    const renderDetailsContent = () =>
        file
            ? renderInActionButtonLayout(
                  true,
                  <FileDetails
                      file={file}
                      isNoteFile={isNoteFile}
                      fileExtension={fileExtension}
                      profileId={profileId}
                  />,
              )
            : null;

    const renderFileContent = () =>
        file
            ? renderInActionButtonLayout(
                  file.processingType !== 'audio',
                  <FileContents
                      file={file}
                      onReprocess={() => {
                          reprocess(fileId);
                      }}
                      isReprocessing={isReprocessing}
                      hasFilesEdit={hasFilesEdit}
                      page={page}
                      boundingBoxParams={boundingBoxParams}
                      startTime={startTime}
                  />,
              )
            : null;

    const shouldRenderTabs = file?.processingStatus !== 'not-started';

    return (
        <AnimateLoadedContent isLoading={isLoadingFile || !file || !selectedTab}>
            {() =>
                file ? (
                    <>
                        <ProfileHeaderContainer profileId={profileId}>
                            {shouldRenderTabs ? (
                                <TabView
                                    selectedTab={selectedTab || 'details'}
                                    onChange={(tab) => setSelectedTab(tab as Tab)}
                                    tabs={[
                                        {
                                            title: intl('file'),
                                            value: 'file',
                                            renderTabContent: () => renderFileContent(),
                                        },
                                        {
                                            title: intl('details'),
                                            value: 'details',
                                            renderTabContent: () => renderDetailsContent(),
                                        },
                                    ]}
                                />
                            ) : (
                                renderDetailsContent()
                            )}
                        </ProfileHeaderContainer>
                        {moveFilePrompt.Prompt}
                        {editFileAction.Prompt}
                    </>
                ) : null
            }
        </AnimateLoadedContent>
    );
};

export default React.memo(FileScreen);
